import { useState, useEffect } from 'react';
import { Sparkles, Check, X, ArrowRight, Trophy, RotateCcw, Star, Gem, ArrowLeft, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { motion, AnimatePresence } from 'framer-motion';
import { crystals } from '@/data/crystalData';
import { supabase } from '@/integrations/supabase/client';
import { useGameSounds } from '@/hooks/useGameSounds';

interface PropertyQuestion {
  crystal: typeof crystals[0];
  property: string;
  options: string[];
  correctAnswer: string;
  questionType: 'crystal-to-property' | 'property-to-crystal';
}

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function generatePropertyQuestions(count: number): PropertyQuestion[] {
  const questions: PropertyQuestion[] = [];
  const shuffledCrystals = shuffleArray(crystals);
  
  for (let i = 0; i < Math.min(count, shuffledCrystals.length); i++) {
    const crystal = shuffledCrystals[i];
    const questionType = Math.random() > 0.5 ? 'crystal-to-property' : 'property-to-crystal';
    
    if (questionType === 'crystal-to-property') {
      // "Which property belongs to [Crystal]?"
      const correctProperty = crystal.properties[0];
      const wrongProperties = shuffleArray(
        crystals
          .filter(c => c.id !== crystal.id)
          .flatMap(c => c.properties)
          .filter(p => p !== correctProperty)
      ).slice(0, 3);
      
      questions.push({
        crystal,
        property: correctProperty,
        options: shuffleArray([correctProperty, ...wrongProperties]),
        correctAnswer: correctProperty,
        questionType
      });
    } else {
      // "Which crystal has this property?"
      const property = crystal.properties[0];
      const wrongCrystals = shuffleArray(
        crystals.filter(c => c.id !== crystal.id && !c.properties.includes(property))
      ).slice(0, 3).map(c => c.name);
      
      questions.push({
        crystal,
        property,
        options: shuffleArray([crystal.name, ...wrongCrystals]),
        correctAnswer: crystal.name,
        questionType
      });
    }
  }
  
  return questions;
}

interface CrystalPropertiesGameProps {
  onBack?: () => void;
}

export function CrystalPropertiesGame({ onBack }: CrystalPropertiesGameProps) {
  const [gameState, setGameState] = useState<'menu' | 'playing' | 'result'>('menu');
  const [questions, setQuestions] = useState<PropertyQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [xpEarned, setXpEarned] = useState(0);
  const [streak, setStreak] = useState(0);
  const { playSound, preloadSounds, soundsEnabled, toggleSounds } = useGameSounds();

  useEffect(() => {
    preloadSounds(['correct', 'incorrect', 'levelComplete', 'streak']);
  }, [preloadSounds]);

  const startGame = (count: number) => {
    setQuestions(generatePropertyQuestions(count));
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setXpEarned(0);
    setStreak(0);
    setGameState('playing');
  };

  const handleAnswer = async (answer: string) => {
    if (selectedAnswer) return;
    
    setSelectedAnswer(answer);
    const current = questions[currentIndex];
    const isCorrect = answer === current.correctAnswer;

    if (isCorrect) {
      const streakBonus = streak >= 3 ? 5 : streak >= 2 ? 3 : 0;
      setScore(score + 1);
      setXpEarned(xpEarned + 10 + streakBonus);
      setStreak(streak + 1);
      
      if (streak >= 2) {
        playSound('streak');
      } else {
        playSound('correct');
      }
    } else {
      setStreak(0);
      playSound('incorrect');
    }

    setShowResult(true);

    // Save progress
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('crystal_quiz_answers').insert({
          user_id: user.id,
          crystal_name: current.crystal.name,
          question_type: 'property',
          question: current.questionType === 'crystal-to-property' 
            ? `Property of ${current.crystal.name}` 
            : `Crystal with: ${current.property}`,
          user_answer: answer,
          correct_answer: current.correctAnswer,
          is_correct: isCorrect,
          xp_awarded: isCorrect ? 10 : 0
        });
      }
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  const nextQuestion = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      finishGame();
    }
  };

  const finishGame = async () => {
    setGameState('result');
    playSound('levelComplete');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: existing } = await supabase
        .from('crystal_learning_progress')
        .select('*')
        .eq('user_id', user.id)
        .single();

      const today = new Date().toISOString().split('T')[0];
      const newXp = (existing?.xp_earned || 0) + xpEarned;

      if (existing) {
        await supabase.from('crystal_learning_progress').update({
          total_quizzes_completed: existing.total_quizzes_completed + 1,
          correct_answers: existing.correct_answers + score,
          xp_earned: newXp,
          last_quiz_date: today
        }).eq('user_id', user.id);
      } else {
        await supabase.from('crystal_learning_progress').insert({
          user_id: user.id,
          total_quizzes_completed: 1,
          correct_answers: score,
          xp_earned: xpEarned,
          last_quiz_date: today
        });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const current = questions[currentIndex];

  return (
    <div className="space-y-6">
      <AnimatePresence mode="wait">
        {gameState === 'menu' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-4"
          >
            <Card className="bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
              <CardHeader className="text-center pb-2">
                <CardTitle className="flex items-center justify-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Crystal Properties
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Learn what each crystal does and its healing benefits
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {onBack && (
                  <Button onClick={onBack} variant="ghost" className="w-full mb-2">
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back to Learning Hub
                  </Button>
                )}
                <Button onClick={() => startGame(5)} className="w-full" size="lg">
                  Quick Round (5 questions)
                </Button>
                <Button onClick={() => startGame(10)} variant="outline" className="w-full">
                  Full Round (10 questions)
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {gameState === 'playing' && current && (
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="space-y-4"
          >
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Question {currentIndex + 1} of {questions.length}
              </span>
              <div className="flex items-center gap-2">
                {streak >= 2 && <span className="text-secondary">🔥 {streak} streak</span>}
                <span className="text-primary font-medium">+{xpEarned} XP</span>
              </div>
            </div>
            <Progress value={((currentIndex + 1) / questions.length) * 100} className="h-2" />

            {/* Crystal Image for context */}
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-muted/50 to-background">
                  <img 
                    src={current.crystal.image} 
                    alt={current.crystal.name}
                    className="w-16 h-16 object-contain"
                  />
                  <div>
                    <p className="text-lg font-medium text-foreground">
                      {current.questionType === 'crystal-to-property' 
                        ? `What is a key property of ${current.crystal.name}?`
                        : 'Which crystal has this property?'}
                    </p>
                    {current.questionType === 'property-to-crystal' && (
                      <p className="text-primary font-medium mt-1">
                        "{current.property}"
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Options */}
            <div className="space-y-2">
              {current.options.map((option, i) => {
                const isCorrect = option === current.correctAnswer;
                const isSelected = option === selectedAnswer;
                
                let className = 'w-full justify-start text-left p-4 h-auto';
                if (showResult) {
                  if (isCorrect) className += ' bg-accent/20 border-accent';
                  else if (isSelected) className += ' bg-destructive/20 border-destructive';
                }

                return (
                  <Button
                    key={i}
                    variant="outline"
                    className={className}
                    onClick={() => handleAnswer(option)}
                    disabled={!!selectedAnswer}
                  >
                    <span className="flex items-center gap-3 w-full">
                      <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs">
                        {String.fromCharCode(65 + i)}
                      </span>
                      <span className="flex-1">{option}</span>
                      {showResult && isCorrect && <Check className="h-4 w-4 text-accent" />}
                      {showResult && isSelected && !isCorrect && <X className="h-4 w-4 text-destructive" />}
                    </span>
                  </Button>
                );
              })}
            </div>

            {/* Feedback */}
            <AnimatePresence>
              {showResult && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className={`border-2 ${
                    selectedAnswer === current.correctAnswer 
                      ? 'bg-accent/10 border-accent/30' 
                      : 'bg-destructive/5 border-destructive/20'
                  }`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        {selectedAnswer === current.correctAnswer ? (
                          <div className="p-2 rounded-full bg-accent/20">
                            <Check className="h-4 w-4 text-accent" />
                          </div>
                        ) : (
                          <div className="p-2 rounded-full bg-destructive/20">
                            <X className="h-4 w-4 text-destructive" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">
                            {selectedAnswer === current.correctAnswer 
                              ? `Correct! +${10 + (streak >= 3 ? 5 : streak >= 2 ? 3 : 0)} XP`
                              : `The answer is: ${current.correctAnswer}`}
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            <strong>{current.crystal.name}</strong>: {current.crystal.properties.slice(0, 2).join(', ')}.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Button onClick={nextQuestion} className="w-full mt-4">
                    {currentIndex < questions.length - 1 ? (
                      <>Next <ArrowRight className="h-4 w-4 ml-2" /></>
                    ) : (
                      <>Results <Trophy className="h-4 w-4 ml-2" /></>
                    )}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {gameState === 'result' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <Card className="bg-gradient-to-br from-primary/20 to-accent/10 border-primary/30">
              <CardHeader className="text-center">
                <div className="mx-auto p-4 rounded-full bg-primary/20 w-fit mb-4">
                  <Trophy className="h-10 w-10 text-primary" />
                </div>
                <CardTitle className="text-2xl">
                  {score === questions.length ? 'Perfect!' : 'Complete!'}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-background/50 p-4">
                    <p className="text-3xl font-bold text-accent">{score}/{questions.length}</p>
                    <p className="text-sm text-muted-foreground">Correct</p>
                  </Card>
                  <Card className="bg-background/50 p-4">
                    <p className="text-3xl font-bold text-primary">+{xpEarned}</p>
                    <p className="text-sm text-muted-foreground">XP Earned</p>
                  </Card>
                </div>

                <Progress value={(score / questions.length) * 100} className="h-3" />
                <p className="text-lg font-semibold">{Math.round((score / questions.length) * 100)}% Accuracy</p>

                {score === questions.length && (
                  <div className="flex items-center justify-center gap-2 text-accent">
                    <Star className="h-5 w-5 fill-current" />
                    <span>Property Master!</span>
                    <Star className="h-5 w-5 fill-current" />
                  </div>
                )}

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setGameState('menu')} className="flex-1">
                    <RotateCcw className="h-4 w-4 mr-2" /> Menu
                  </Button>
                  <Button onClick={() => startGame(5)} className="flex-1">
                    <Sparkles className="h-4 w-4 mr-2" /> Again
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
